/**
 * Shared presentational components.
 *
 * All of them render against `src/styles/app.css`, which is the prototype's
 * stylesheet copied verbatim. Where it already names a component — `.page-head`,
 * `.card-x` with `.card-head`/`.card-body-x`, `.stat`, `.av`, `.bar` — these use
 * those class names rather than a parallel set. Inventing `.stat-tile` next to a
 * `.stat` that already exists produced two design systems that drifted, and the
 * second one had to be maintained by hand in `react-overrides.css`.
 *
 * Kept in one barrel file because each is small and they are almost always
 * imported together. Split any one out the moment it grows its own state or
 * needs its own tests.
 */
import { createPortal } from 'react-dom';
import {
  useEffect, useLayoutEffect, useRef, useState,
} from 'react';
import { initials as toInitials, avatarTone } from '../../utils/formatters.js';

/* -------------------------------------------------------------------------- */
/* Page furniture                                                              */
/* -------------------------------------------------------------------------- */

/**
 * The heading block every screen opens with: title, optional subtitle and
 * breadcrumb, and a slot for the primary actions.
 *
 * @param {{title:string, subtitle?:string, breadcrumb?:{label:string,to?:string}[],
 *          children?:React.ReactNode, badge?:React.ReactNode}} props
 */
export function PageHeader({ title, subtitle, breadcrumb, badge, children }) {
  return (
    <div className="page-head">
      <div>
        {breadcrumb?.length > 0 && (
          <nav className="t-xs ink-3 mb-1" aria-label="Breadcrumb">
            {breadcrumb.map((crumb, i) => (
              <span key={crumb.label}>
                {i > 0 && <span className="mx-1">/</span>}
                {crumb.label}
              </span>
            ))}
          </nav>
        )}
        <h1 className="page-title d-flex align-items-center gap-2">
          {title}
          {badge}
        </h1>
        {subtitle && <p className="page-sub mb-0">{subtitle}</p>}
      </div>
      {children && <div className="ms-auto d-flex flex-wrap align-items-center gap-2">{children}</div>}
    </div>
  );
}

/**
 * A surface with an optional header row. The workhorse container.
 *
 * `.card-x` > `.card-head` / `.card-body-x` / `.card-foot` is the prototype's
 * own structure, and `app.css` styles `.card-x .card-head h5` directly — which
 * is why the title is an `h5` and not a class. `bare` skips the body padding for
 * a card whose content is a full-width table.
 */
export function Card({
  title, subtitle, actions, footer, children,
  className = '', bodyClass = '', bare = false, style, headStyle,
}) {
  return (
    <section className={`card-x ${className}`} style={style}>
      {(title || actions) && (
        <div className="card-head" style={headStyle}>
          <div>
            {title && <h5 className="mb-0">{title}</h5>}
            {subtitle && <div className="t-xs ink-3">{subtitle}</div>}
          </div>
          {actions && <div className="ms-auto d-flex align-items-center gap-2">{actions}</div>}
        </div>
      )}
      {bare ? children : <div className={`card-body-x ${bodyClass}`}>{children}</div>}
      {footer && <div className="card-foot d-flex align-items-center">{footer}</div>}
    </section>
  );
}

/**
 * A KPI tile — the prototype's `.stat`, with its gradient icon, tinted corner
 * wash and `.trend` pill.
 *
 * `delta` is rendered green for positive and red for negative **except** where
 * `invertDelta` is set: for "average sales cycle" or "deals slipping", down is
 * the good direction, and colouring a fall red would read as an alarm about an
 * improvement.
 *
 * `meta` is the line the prototype puts under every value — "17 deals ·
 * weighted $486K". It carries the context that stops a number being read alone.
 */
const STAT_TONES = {
  brand:   { grad: 'var(--grad-brand)', tint: '#eef2ff' },
  sky:     { grad: 'var(--grad-cool)',  tint: '#f0f9ff' },
  emerald: { grad: 'var(--grad-mint)',  tint: '#ecfdf5' },
  amber:   { grad: 'var(--grad-warm)',  tint: '#fffbeb' },
  violet:  { grad: 'linear-gradient(135deg,#8b5cf6,#d946ef)', tint: '#f5f3ff' },
  rose:    { grad: 'linear-gradient(135deg,#f43f5e,#ec4899)', tint: '#fff1f2' },
};

export function StatTile({
  label, value, suffix, delta, deltaSuffix = '%', invertDelta = false,
  tone = 'brand', icon = 'bi-graph-up-arrow', meta, className = '',
}) {
  const { grad, tint } = STAT_TONES[tone] ?? STAT_TONES.brand;
  const rising = delta > 0;
  const good = invertDelta ? delta < 0 : rising;

  return (
    <div className={`stat ${className}`} style={{ '--tint': tint }}>
      <div className="d-flex justify-content-between align-items-start mb-2">
        <div className="stat-ico" style={{ background: grad }}>
          <i className={`bi ${icon}`} aria-hidden="true" />
        </div>
        {delta != null && delta !== 0 && (
          <span className={`trend ${good ? 'up' : 'down'}`}>
            <i className={`bi ${rising ? 'bi-arrow-up-short' : 'bi-arrow-down-short'}`} aria-hidden="true" />
            {Math.abs(delta)}{deltaSuffix}
          </span>
        )}
        {delta === 0 && <span className="trend flat">no change</span>}
      </div>
      <div className="stat-value">
        {value}
        {suffix && <span className="t-md ink-4 fw-550">{suffix}</span>}
      </div>
      <div className="stat-label mt-1">{label}</div>
      {meta && <div className="stat-meta mt-2">{meta}</div>}
    </div>
  );
}

/* --------------------------------------------------------------------------
   Loading states

   Defined in ./Loading.jsx and re-exported here, because every screen already
   imports `Spinner` from this barrel and a loading state is not worth a second
   import path. Skeletons live beside it: choosing between the two is the same
   decision, made in one place.
   -------------------------------------------------------------------------- */
export {
  Spinner, ButtonSpinner, PageLoader, TopProgressBar,
  Skeleton, SkeletonText, SkeletonTable, SkeletonKpiRow,
  SkeletonChart, SkeletonCard, SkeletonDashboard,
} from './Loading.jsx';

/* -------------------------------------------------------------------------- */
/* Small pieces                                                                */
/* -------------------------------------------------------------------------- */

/**
 * A status pill.
 *
 * The tone names screens use are not all class names: `app.css` calls them
 * `pill-gray` and `pill-green`, while the code around here says `neutral` and
 * `emerald` — the same words the API's `tone` fields use. Mapping the two here
 * is one line; the alternative was a second set of `.pill-*` rules in
 * `react-overrides.css`, which is exactly the duplication that made every badge
 * in this build subtly off-palette.
 */
const PILL_TONE = {
  neutral: 'gray',
  emerald: 'green',
  green: 'green',
  gray: 'gray',
  brand: 'brand',
  sky: 'sky',
  amber: 'amber',
  rose: 'rose',
  violet: 'violet',
  teal: 'teal',
};

export function Badge({ children, tone = 'neutral', className = '' }) {
  return <span className={`pill pill-${PILL_TONE[tone] ?? 'gray'} ${className}`}>{children}</span>;
}

/**
 * Initials in a tinted circle — the prototype's `.av`, with the tint hashed from
 * the name so the same person is the same colour on every screen.
 *
 * `size` still accepts a number, because callers pass 22, 27, 32, 42; it picks
 * the nearest `.av-*` step and only writes inline dimensions for a size the
 * stylesheet has no step for.
 */
const AV_STEPS = [[22, 'av-xs'], [27, 'av-sm'], [34, ''], [42, 'av-lg'], [60, 'av-xl']];

export function Avatar({ name, size = 34, src, className = '' }) {
  const step = AV_STEPS.find(([px]) => px === size);
  const sizeClass = step ? step[1] : '';
  const style = step ? undefined : { width: size, height: size, fontSize: Math.round(size * 0.38) };

  if (src) {
    return <img className={`av ${sizeClass} ${className}`} style={style} src={src} alt={name} />;
  }
  return (
    <span className={`av ${sizeClass} ${avatarTone(name)} ${className}`} style={style} title={name} aria-label={name}>
      {toInitials(name)}
    </span>
  );
}

/**
 * A horizontal meter — the prototype's `.bar`, used for quota attainment and
 * score breakdowns.
 *
 * The prototype animates these from zero by setting `width` from a `data-w`
 * attribute after paint. Here the width is just rendered: React re-renders on
 * data arriving, and `.bar > span` already carries a 600ms width transition, so
 * the fill animates on mount without a second pass over the DOM.
 */
const METER_FILL = { brand: '', mint: 'g-mint', warm: 'g-warm', cool: 'g-cool' };

export function Meter({ value, max = 100, tone = 'brand', showValue = false, thickness, className = '' }) {
  const pct = max > 0 ? Math.min(100, Math.max(0, (value / max) * 100)) : 0;
  /* `flex-grow-1` on the root, not only on the track inside it.
     A caller that sets a label of its own puts this beside that label in a flex
     row, which makes this div a flex item with no basis — the track's own
     `flex-grow-1` then has nothing to grow into and the bar measures 0px wide.
     The table cell still showed its percentage, so the column looked designed
     that way rather than broken. In a block parent the property is ignored. */
  return (
    <div className={`d-flex align-items-center gap-2 flex-grow-1 ${className}`}>
      <div
        className={`bar flex-grow-1 ${thickness ?? ''}`}
        role="progressbar"
        aria-valuenow={Math.round(pct)}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <span className={METER_FILL[tone] ?? ''} style={{ width: `${pct}%` }} />
      </div>
      {showValue && <span className="t-xs ink-3 tabular" style={{ minWidth: 34 }}>{Math.round(pct)}%</span>}
    </div>
  );
}


/**
 * What a screen shows when there is nothing to show.
 *
 * Takes an action slot, because an empty list is usually a prompt rather than a
 * dead end — "No leads match these filters" wants a Clear filters button next to it.
 */
export function EmptyState({ icon = 'bi-inbox', title, message, children }) {
  return (
    <div className="empty-state text-center py-5">
      <i className={`bi ${icon}`} aria-hidden="true" />
      <h3 className="t-md fw-600 mt-2 mb-1">{title}</h3>
      {message && <p className="t-sm text-muted mb-3">{message}</p>}
      {children}
    </div>
  );
}

/**
 * A failed request, rendered in place rather than thrown away.
 *
 * Shows the `request_id` when the API supplied one: that is the value that
 * matches a row in `audit.system_log`, and quoting it turns "it broke" into a
 * support ticket someone can actually act on.
 */
export function ErrorState({ error, onRetry }) {
  if (!error) return null;
  return (
    <div className="alert-x alert-danger-x" role="alert">
      <div className="d-flex align-items-start gap-2">
        <i className="bi bi-exclamation-triangle-fill mt-1" aria-hidden="true" />
        <div className="flex-grow-1">
          <div className="fw-600">{error.message ?? 'Something went wrong.'}</div>
          {error.details?.length > 0 && (
            <ul className="t-xs mb-0 mt-1 ps-3">
              {error.details.map((d, i) => (
                <li key={i}>{d.field ? `${d.field}: ` : ''}{d.message ?? d.code}</li>
              ))}
            </ul>
          )}
          {error.requestId && (
            <div className="t-2xs text-muted mt-1">
              Reference <code>{error.requestId}</code>
            </div>
          )}
        </div>
        {onRetry && (
          <button type="button" className="btn-x btn-ghost btn-sm-x" onClick={onRetry}>
            Retry
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * Marks a screen as showing fixture data because its API module is not built.
 *
 * Deliberately visible rather than a console line. Someone demoing this must not
 * be able to mistake a fixture for a live figure — that is how a number nobody
 * verified ends up in a board pack.
 */
export function MockBadge({ section }) {
  return (
    <span className="pill pill-amber" title={`The API answered 501 for ${section}. These are fixtures from src/api/mock.`}>
      <i className="bi bi-database-exclamation me-1" aria-hidden="true" />
      Sample data
    </span>
  );
}

/* -------------------------------------------------------------------------- */
/* Inputs                                                                      */
/* -------------------------------------------------------------------------- */

/** A search box with the leading icon and clear affordance the prototype uses. */
export function SearchInput({ value, onChange, placeholder = 'Search…', className = '' }) {
  return (
    <div className={`search-x ${className}`}>
      <i className="bi bi-search" aria-hidden="true" />
      <input
        type="search"
        className="form-control"
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        aria-label={placeholder}
      />
      {value && (
        <button type="button" className="search-clear" onClick={() => onChange('')} aria-label="Clear search">
          <i className="bi bi-x-lg" aria-hidden="true" />
        </button>
      )}
    </div>
  );
}

/**
 * A `.chip` button that opens a single-select dropdown menu — several
 * prototype screens (`accounts.html`, `contacts.html`) render their Industry/
 * Region/Account/Influence filters this way rather than as a native
 * `<select>`. No Bootstrap JS is loaded, so `.show` is toggled by hand, same
 * pattern as `LeadsListPage`'s `OverflowMenu`/`ListViewPicker`.
 */
/**
 * @param {boolean} [disabled] pass when the caller's data scope makes the
 * filter meaningless — e.g. an "Owner" picker for a user whose `own`-scoped
 * permission means RLS already limits every result to themselves, so picking
 * someone else could only ever come back empty. `disabledReason` explains why,
 * as a tooltip, rather than the control silently vanishing.
 */
export function ChipSelect({ label, value, onChange, options, allLabel, disabled = false, disabledReason }) {
  const [open, setOpen] = useState(false);
  const [alignRight, setAlignRight] = useState(false);
  const box = useRef(null);
  const menu = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const away = (e) => { if (!box.current?.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', away);
      document.removeEventListener('keydown', esc);
    };
  }, [open]);

  /* Bootstrap's plain CSS left-aligns `.dropdown-menu` to its `.dropdown`
   * parent with no Popper to flip it — fine for a chip with room to its
   * right, but a filter bar packs several chips against the right edge, so
   * the menu ran past the viewport and forced the whole page to scroll
   * horizontally. Flip to right-aligned whenever the left-aligned menu
   * wouldn't fit, measured after it's actually rendered (open, not disabled)
   * so `offsetWidth` reflects its real width rather than the `minWidth` guess. */
  useLayoutEffect(() => {
    if (!open || !box.current || !menu.current) return;
    const { left } = box.current.getBoundingClientRect();
    setAlignRight(left + menu.current.offsetWidth > window.innerWidth - 8);
  }, [open]);

  const selected = options.find((opt) => (typeof opt === 'string' ? opt : opt.value) === value);
  const selectedLabel = selected ? (typeof selected === 'string' ? selected : selected.label) : null;

  return (
    <div className="dropdown" ref={box}>
      <button
        type="button"
        className={`chip ${value ? 'active' : ''}`}
        onClick={() => !disabled && setOpen((v) => !v)}
        aria-expanded={open}
        disabled={disabled}
        title={disabled ? disabledReason : undefined}
      >
        {selectedLabel ? `${label}: ${selectedLabel}` : label}
        {value && !disabled
          ? <i className="bi bi-x-lg x" aria-hidden="true" onClick={(e) => { e.stopPropagation(); onChange(''); }} />
          : <i className="bi bi-chevron-down x" aria-hidden="true" />}
      </button>
      {!disabled && (
        <ul
          ref={menu}
          className={`dropdown-menu ${open ? 'show' : ''}`}
          style={{ minWidth: 200, ...(alignRight ? { left: 'auto', right: 0 } : {}) }}
        >
          <li>
            <button type="button" className="dropdown-item" onClick={() => { onChange(''); setOpen(false); }}>
              {allLabel}
            </button>
          </li>
          <li><hr className="dropdown-divider" /></li>
          {options.map((opt) => {
            const key = typeof opt === 'string' ? opt : opt.value;
            const text = typeof opt === 'string' ? opt : opt.label;
            return (
              <li key={key}>
                <button
                  type="button"
                  className={`dropdown-item ${value === key ? 'active' : ''}`}
                  onClick={() => { onChange(key); setOpen(false); }}
                >
                  {text}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/** A labelled `<select>` for the filter bars. */
export function FilterSelect({ label, value, onChange, options, allLabel = 'All' }) {
  return (
    <label className="filter-x">
      <span className="filter-x-label">{label}</span>
      <select className="form-select" value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">{allLabel}</option>
        {options.map((opt) => {
          const key = typeof opt === 'string' ? opt : opt.value;
          const text = typeof opt === 'string' ? opt : opt.label;
          return <option key={key} value={key}>{text}</option>;
        })}
      </select>
    </label>
  );
}

/**
 * A single-select toggle-chip row for the filter bars — the Leads screen's
 * filter-rail look (`.chip`/`.chip.active`, see LeadsListPage.jsx's
 * `FilterRail`) rather than a plain `<select>`. Click a chip to filter to it;
 * click the active one again to clear back to "all" (`value` becomes `''`).
 * `options` accepts plain strings or `{value,label}` pairs, same as
 * `FilterSelect`, for a filter whose value ('true'/'false') isn't fit to
 * display as-is.
 */
export function ChipFilter({ label, value, onChange, options }) {
  return (
    <div className="filter-x">
      <span className="filter-x-label">{label}</span>
      <div className="d-flex flex-wrap gap-1">
        {options.map((opt) => {
          const key = typeof opt === 'string' ? opt : opt.value;
          const text = typeof opt === 'string' ? opt : opt.label;
          return (
            <button
              key={key}
              type="button"
              className={`chip ${value === key ? 'active' : ''}`}
              aria-pressed={value === key}
              onClick={() => onChange(value === key ? '' : key)}
            >
              {text}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * A form field with its label, help text and error in one place.
 *
 * `error` is the message react-hook-form produced — usually mapped straight from
 * the API's `details[]`, so a server-side rule and a client-side rule surface
 * identically to the user.
 */
export function Field({ label, error, help, required, children, className = '' }) {
  return (
    <div className={`field-x ${error ? 'has-error' : ''} ${className}`}>
      {label && (
        <label className="field-x-label">
          {label}
          {required && <span className="text-danger ms-1" aria-hidden="true">*</span>}
        </label>
      )}
      {children}
      {error && <div className="field-x-error">{error}</div>}
      {!error && help && <div className="field-x-help">{help}</div>}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Overlays                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * A modal dialog.
 *
 * Closes on Escape and restores focus to whatever opened it. Rendered in a
 * portal on `document.body`: `position: fixed` is measured against the nearest
 * ancestor with a transform/filter, and cards here carry `.fade-up` animations,
 * so a modal rendered inline inside one had its backdrop clipped to that card
 * (a grey patch over one column instead of the whole screen). React events
 * still bubble through the component tree, so handlers behave as before.
 */
export function Modal({ open, onClose, title, children, footer, size = '' }) {
  const openerRef = useRef(null);
  /* `onClose` lives in a ref so the effect below depends on `open` alone. It used
     to depend on `onClose` too, and a caller that passes an inline function (new
     identity every render — any dialog holding its form state itself) re-ran the
     effect on every keystroke; its cleanup restores focus to the opener, which
     yanked the cursor out of whatever field was being typed in. */
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return undefined;
    openerRef.current = document.activeElement;
    const onKey = (e) => e.key === 'Escape' && onCloseRef.current?.();
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
      openerRef.current?.focus?.();
    };
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div className="modal-x-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose?.()}>
      <div className={`modal-x ${size}`} role="dialog" aria-modal="true" aria-label={title}>
        <header className="modal-x-head">
          <h2 className="card-x-title mb-0">{title}</h2>
          <button type="button" className="btn-close btn-sm" onClick={onClose} aria-label="Close" />
        </header>
        <div className="modal-x-body">{children}</div>
        {footer && <footer className="modal-x-foot">{footer}</footer>}
      </div>
    </div>,
    document.body,
  );
}

/** A right-hand slide-over, for record detail without leaving the list. */
export function Drawer({ open, onClose, title, subtitle, children, footer, width = 520 }) {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === 'Escape' && onCloseRef.current?.();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div className="drawer-x-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose?.()}>
      <aside className="drawer-x" style={{ width }} role="dialog" aria-modal="true" aria-label={title}>
        <header className="drawer-x-head">
          <div>
            <h2 className="card-x-title mb-0">{title}</h2>
            {subtitle && <p className="card-x-sub mb-0">{subtitle}</p>}
          </div>
          <button type="button" className="btn-close btn-sm" onClick={onClose} aria-label="Close" />
        </header>
        <div className="drawer-x-body">{children}</div>
        {footer && <footer className="drawer-x-foot">{footer}</footer>}
      </aside>
    </div>,
    document.body,
  );
}

/* -------------------------------------------------------------------------- */
/* Tabs                                                                        */
/* -------------------------------------------------------------------------- */

/**
 * A tab strip. Controlled if `value`/`onChange` are supplied, otherwise it keeps
 * its own state — most screens only need the latter.
 *
 * @param {{tabs:{key:string,label:string,badge?:React.ReactNode}[], value?:string,
 *          onChange?:(key:string)=>void, defaultTab?:string}} props
 */
export function Tabs({ tabs, value, onChange, defaultTab }) {
  const [internal, setInternal] = useState(defaultTab ?? tabs[0]?.key);
  const active = value ?? internal;
  const select = (key) => (onChange ? onChange(key) : setInternal(key));

  return (
    <div className="tabs-x" role="tablist">
      {tabs.map((tab) => (
        <button
          key={tab.key}
          type="button"
          role="tab"
          aria-selected={active === tab.key}
          className={`tab-x ${active === tab.key ? 'is-active' : ''}`}
          onClick={() => select(tab.key)}
        >
          {tab.label}
          {tab.badge != null && <span className="tab-x-badge">{tab.badge}</span>}
        </button>
      ))}
    </div>
  );
}
